/**
 * Choosing what a new role is for, without React.
 *
 * A role created with nothing attached has no effect anybody can see, so the
 * form picks its features and permissions and they are granted with it, in one
 * transaction. This is the part that decides what may be picked, and keeps the
 * two kinds in step.
 *
 * Two rules, both mirrors of the server's:
 *
 * - a permission stands on its feature (ADR-0019), so switching a feature off
 *   takes the permissions under it with it. Sending one without the other is a
 *   malformed request, not a decision to weigh;
 * - nobody grants what they do not hold themselves. `cellLock` already says
 *   this for an existing role; a new one is never a role the actor holds, so
 *   the own-role arm of it never applies.
 */

import type { DepartmentId } from "@wallyo/contracts/workspaces";
import type { GetStaffMeResponse, StaffRoleListItem } from "@wallyo/contracts/console";
import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { DefinedPermissionId } from "@wallyo/contracts/permissions";

import { t } from "../../i18n";
import { SCREENS, canOpen, type Screen } from "../../navigation/screens";
import { REASON_MIN_LENGTH } from "../team/team-writes";
import {
  cellLock,
  featureGroups,
  permissionCellLock,
  permissionRows,
  type CellLock,
  type GridFeature,
  type GridGroup,
  type GridPermission,
  type PermissionLock,
} from "../team/team-access";

/** What the form has picked so far. */
export type RoleDraft = {
  features: readonly StaffFeatureId[];
  permissions: readonly DefinedPermissionId[];
};

export const EMPTY_ROLE_DRAFT: RoleDraft = { features: [], permissions: [] };

/**
 * A role that exists but grants nothing, which is what a new one is.
 *
 * `cellLock` and `permissionLock` are written against a role, and the only
 * thing they read from it is what it already grants and whether the actor
 * holds it. A role being created is held by nobody and grants nothing, so this
 * answers both without a second copy of the rules.
 */
const NEW_ROLE: StaffRoleListItem = {
  staffRoleId: "",
  name: "",
  description: null,
  department: null,
  features: [],
  permissions: [],
  memberCount: 0,
  createdAt: "",
  version: 1,
};

export function groups(): GridGroup[] {
  return featureGroups();
}

export function lockOf(me: GetStaffMeResponse, feature: GridFeature): CellLock {
  return cellLock(me, NEW_ROLE, feature);
}

export function permissionsOf(settingId: StaffFeatureId): GridPermission[] {
  return permissionRows(settingId);
}

export function permissionLockOf(me: GetStaffMeResponse, permission: GridPermission): PermissionLock {
  return permissionCellLock(me, NEW_ROLE, permission);
}

export const hasFeature = (draft: RoleDraft, settingId: StaffFeatureId): boolean => draft.features.includes(settingId);

export const hasPermission = (draft: RoleDraft, settingId: DefinedPermissionId): boolean =>
  draft.permissions.includes(settingId);

/**
 * Switches a feature on or off.
 *
 * Off takes the permissions under it: they stand on it, and the server refuses
 * a permission whose feature is not being granted.
 */
export function withFeature(draft: RoleDraft, settingId: StaffFeatureId, on: boolean): RoleDraft {
  if (on) {
    return hasFeature(draft, settingId) ? draft : { ...draft, features: [...draft.features, settingId] };
  }
  const under = new Set(permissionRows(settingId).map((permission) => permission.settingId));
  return {
    features: draft.features.filter((each) => each !== settingId),
    permissions: draft.permissions.filter((each) => !under.has(each)),
  };
}

/** Switches a permission on or off. On brings its feature with it. */
export function withPermission(draft: RoleDraft, permission: GridPermission, on: boolean): RoleDraft {
  if (!on) {
    return { ...draft, permissions: draft.permissions.filter((each) => each !== permission.settingId) };
  }
  const withIt = hasPermission(draft, permission.settingId)
    ? draft.permissions
    : [...draft.permissions, permission.settingId];
  return { features: withFeature(draft, permission.feature, true).features, permissions: withIt };
}

/** What the create request says, in catalogue order so the same picks send the same body. */
export function createRequest(
  draft: RoleDraft,
  name: string,
  description: string,
  department: DepartmentId,
  reason: string,
) {
  const order = groups().flatMap((group) => group.features.map((feature) => feature.settingId));
  const rank = (settingId: string) => {
    const at = order.indexOf(settingId as StaffFeatureId);
    return at === -1 ? order.length : at;
  };
  return {
    name: name.trim(),
    description: description.trim() || null,
    department,
    features: [...draft.features].sort((one, other) => rank(one) - rank(other)),
    permissions: [...draft.permissions].sort((one, other) => rank(one) - rank(other)),
    reason: reason.trim(),
  };
}

/** What stops the form, said before it is sent rather than after it is refused. */
export function createProblem(
  draft: RoleDraft,
  name: string,
  department: DepartmentId | "",
  reason: string,
): string | null {
  if (name.trim().length === 0) return t("roles.new.nameNeeded");
  // Every role belongs somewhere. A QA tester is Product & Engineering; an
  // accountant is Finance. One created without saying which opens nothing.
  if (department === "") return t("roles.new.departmentNeeded");
  if (draft.features.length === 0 && draft.permissions.length === 0) return t("roles.new.grantsNeeded");
  if (reason.trim().length < REASON_MIN_LENGTH) return t("features.save.reasonNeeded");
  return null;
}

/**
 * The screens a role with these picks would open.
 *
 * Deliberately computed rather than stored. A role does not carry a list of
 * screens: a screen names the features that open it, and `canOpen` answers
 * from that — the same function the shell uses to draw the menu, and the same
 * features the API checks on every action. A stored list would be a second
 * thing to keep in step, and it would only hide a menu item: the endpoint
 * would still answer.
 */
export function opensScreens(draft: RoleDraft): readonly { screen: Screen; opens: boolean }[] {
  const features = draft.features as readonly StaffFeatureId[];
  // Workspace front doors are left out: they gate on nothing, so every role
  // would appear to open all eight (WALLIYO-0132).
  return SCREENS.filter((screen) => !screen.overview).map((screen) => ({
    screen,
    opens: canOpen(screen, features),
  }));
}
